/**
 * PriceTracePanel (S9.2) — THE shared price-trace surface (holistic plan
 * §S9.2, net-new shared component list). One component, three consumers:
 * the SO-line affordance (`salesOrders/components/PriceTraceTrigger`), the
 * product 定價 tab, and the standalone 價格模擬器 (`pricing/PriceSimulatorPage`).
 *
 * Two exports:
 * - `PriceTraceContent` — the inline body (the simulator renders it inline).
 * - default `PriceTracePanel` — Drawer width 480 wrapping the content (the
 *   SO-line trigger opens this).
 *
 * Layout (plan §S9.2, in order):
 * 1. Header — final (transacted) price + winning-tier source chip
 *    (特殊價/價格等級/標準售價); manual-override note with BOTH numbers.
 * 2. Tier walk in resolution order — every tier appears exactly once,
 *    matched ✓ / skipped-with-reason (+ rule ref); a matched-but-outranked
 *    tier shows its candidate number AND「lower_precedence」— the anti-175%
 *    artifact made visible.
 * 3. Build-up waterfall table — each row number + formula.
 * 4. Footer — margin vs landed $/% with the red below-floor badge.
 *
 * The resolution fed in is IMMUTABLE: persisted SO-line snapshots are frozen
 * (`snapshot` renders the never-silently-repriced note); the panel only reads.
 */

import { Divider, Drawer, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PurpleTag from '@/components/common/PurpleTag';
import TableContainer, { renderDash } from '@/components/common/TableContainer';
import { evaluateMarginFloor, lineMarginComparable, roundHalfUp2 } from '@/lib/pricing';
import { usePricingStore } from '@/stores/pricingStore';
import type {
  PriceResolution,
  PriceSkipReason,
  PriceWaterfallRow,
  PriceWinner,
  PricingTier,
} from '@/types/api/pricing';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Pure view-model helpers (exported for vitest — priceTracePanel.test.ts)
// ---------------------------------------------------------------------------

/** Tier → `pricing` ns label key (特殊價 / 價格等級 / 標準售價 / 無法計價). */
// eslint-disable-next-line react-refresh/only-export-components
export const TIER_LABEL_KEYS: Record<PriceWinner, string> = {
  special_override: 'pricing:pricing.tiers.specialOverride',
  customer_level: 'pricing:pricing.tiers.customerLevel',
  list_price: 'pricing:pricing.tiers.listPrice',
  unresolved: 'pricing:pricing.tiers.unresolved',
};

/** Skip reason → `pricing` ns label key — every resolver reason has a label. */
// eslint-disable-next-line react-refresh/only-export-components
export const SKIP_REASON_KEYS: Record<PriceSkipReason, string> = {
  no_customer: 'pricing:pricing.skipReasons.noCustomer',
  no_rule_found: 'pricing:pricing.skipReasons.noRuleFound',
  outside_validity: 'pricing:pricing.skipReasons.outsideValidity',
  inactive: 'pricing:pricing.skipReasons.inactive',
  no_price_level_assigned: 'pricing:pricing.skipReasons.noPriceLevelAssigned',
  price_level_inactive: 'pricing:pricing.skipReasons.priceLevelInactive',
  no_band_match: 'pricing:pricing.skipReasons.noBandMatch',
  no_markup_rule: 'pricing:pricing.skipReasons.noMarkupRule',
  no_cost_record: 'pricing:pricing.skipReasons.noCostRecord',
  lower_precedence: 'pricing:pricing.skipReasons.lowerPrecedence',
};

export interface PriceMargin {
  /** price − landedCost, 2dp. */
  amount: number;
  /** amount / price × 100, 2dp. */
  pct: number;
}

/**
 * Margin vs landed for the EFFECTIVE (transacted) price — an override moves
 * the margin, so the footer recomputes off `unitPrice` (falls back to the
 * resolved price). Null when landed cost or price is unknown.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function marginForPrice(
  resolution: PriceResolution,
  unitPrice: number | null
): PriceMargin | null {
  const landed = resolution.waterfall.find((row) => row.key === 'landed_cost')?.amount ?? null;
  const price = unitPrice ?? resolution.unitPrice;
  if (landed === null || price === null || price === 0) return null;
  const amount = roundHalfUp2(price - landed);
  return { amount, pct: roundHalfUp2((amount / price) * 100) };
}

/** True when the effective price sits BELOW landed cost (red badge). */
// eslint-disable-next-line react-refresh/only-export-components
export function isBelowFloor(resolution: PriceResolution, unitPrice: number | null): boolean {
  const margin = marginForPrice(resolution, unitPrice);
  return margin !== null && margin.amount < 0;
}

// Local 2dp formatter — `common/` must not depend on the salesOrders slice's
// soFormat helpers (dependency direction).
const amountFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatAmount = (value: number): string => amountFormatter.format(value);
const formatMoney = (currency: string, value: number): string =>
  `${currency} ${formatAmount(value)}`;

// ---------------------------------------------------------------------------
// Tier walk row
// ---------------------------------------------------------------------------

interface TraceRowProps {
  tier: PricingTier;
  matched: boolean;
  won: boolean;
  candidatePrice?: number;
  ruleRef?: string;
  skipReason?: PriceSkipReason;
}

const TraceRow = ({ tier, matched, won, candidatePrice, ruleRef, skipReason }: TraceRowProps) => {
  const { t } = useTranslation(['pricing']);
  const { token } = theme.useToken();

  const icon = won ? (
    <CheckCircleFilled style={{ color: token.colorPrimary, fontSize: token.sizeSM }} />
  ) : matched ? (
    <CheckCircleOutlined style={{ color: token.colorTextSecondary, fontSize: token.sizeSM }} />
  ) : (
    <MinusCircleOutlined style={{ color: token.colorTextQuaternary, fontSize: token.sizeSM }} />
  );

  return (
    <div className="tw-flex tw-items-start tw-gap-2 tw-py-1">
      {icon}
      <div className="tw-flex-1 tw-min-w-0">
        <Text
          style={{
            display: 'block',
            fontWeight: won ? token.fontWeightStrong : undefined,
            color: matched ? token.colorText : token.colorTextSecondary,
          }}
        >
          {t(TIER_LABEL_KEYS[tier])}
        </Text>
        {skipReason && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t(SKIP_REASON_KEYS[skipReason])}
          </Text>
        )}
        {ruleRef && (
          <Text type="secondary" style={{ display: 'block', fontSize: token.fontSizeSM }}>
            {t('pricing:pricing.trace.rule')}: {ruleRef}
          </Text>
        )}
      </div>
      {candidatePrice !== undefined && (
        <Text
          style={{
            fontVariantNumeric: 'tabular-nums',
            fontWeight: won ? token.fontWeightStrong : undefined,
            color: won ? token.colorText : token.colorTextSecondary,
          }}
        >
          {formatAmount(candidatePrice)}
        </Text>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline content (simulator + product tab render this directly)
// ---------------------------------------------------------------------------

export interface PriceTraceContentProps {
  /** The immutable resolver snapshot the panel explains. */
  resolution: PriceResolution;
  /** Transacted unit price (differs from the resolved one when overridden). */
  unitPrice: number | null;
  /** Display currency (fallback when the resolution carries none). */
  currency: string;
  /** Manual-override reason; presence marks the price as overridden. */
  overrideReason?: string | null;
  /** Persisted SO-line snapshot → renders the never-silently-repriced note. */
  snapshot?: boolean;
  /**
   * Minimum margin (2026-09-15) the footer judges against. `undefined` ⇒
   * read the live company floor from the pricing store (product tab,
   * simulator); a detail page passes the order's snapshotted floor; null ⇒
   * no floor line at all.
   */
  marginFloorPct?: number | null;
  /**
   * The DOCUMENT currency the transacted price is in (an order header). The
   * display currency falls back to the resolution's own, which can never
   * disagree with itself — so comparability read from it was always true and
   * the footer labelled a clean cross-currency line "below minimum"
   * (2026-09-16). Omit on a single-currency surface (the product tab, the
   * simulator), where the display currency IS the document currency.
   */
  documentCurrency?: string | null;
}

export const PriceTraceContent = ({
  resolution,
  unitPrice,
  currency,
  overrideReason,
  snapshot,
  marginFloorPct,
  documentCurrency,
}: PriceTraceContentProps) => {
  const { t } = useTranslation(['pricing']);
  const { token } = theme.useToken();
  const storeFloor = usePricingStore((state) => state.dataset?.minimumMarginPct ?? null);
  const floorPct = marginFloorPct === undefined ? storeFloor : marginFloorPct;

  const overridden = Boolean(overrideReason);
  const displayCurrency = resolution.currency ?? currency;
  const effectivePrice = unitPrice ?? resolution.unitPrice;
  const margin = marginForPrice(resolution, effectivePrice);
  const belowFloor = isBelowFloor(resolution, effectivePrice);
  const landedForFloor =
    resolution.waterfall.find((row) => row.key === 'landed_cost')?.amount ?? null;
  const floor = evaluateMarginFloor({
    unitPrice: effectivePrice,
    landedCost: landedForFloor,
    floorPct,
    comparable: lineMarginComparable({
      landedCost: landedForFloor,
      currency: documentCurrency ?? displayCurrency,
      priceTrace: resolution,
    }),
  });

  const waterfallColumns: TableProps<PriceWaterfallRow>['columns'] = [
    {
      title: t('pricing:pricing.panel.waterfall.item'),
      dataIndex: 'labelKey',
      key: 'item',
      render: (labelKey: string) => t(`pricing:${labelKey}`),
    },
    {
      title: t('pricing:pricing.panel.waterfall.formula'),
      dataIndex: 'formula',
      key: 'formula',
      render: (formula: string | null) =>
        formula ? (
          <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formula}
          </Text>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('pricing:pricing.panel.waterfall.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 110,
      render: (amount: number) => (
        <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(amount)}</Text>
      ),
    },
  ];

  return (
    <div>
      {/* 1 — Header: final price + winning-tier source chip */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <PurpleTag>{t(TIER_LABEL_KEYS[resolution.winner])}</PurpleTag>
        <Text
          strong
          style={{ fontSize: token.fontSizeHeading4, fontVariantNumeric: 'tabular-nums' }}
        >
          {effectivePrice !== null
            ? formatMoney(displayCurrency, effectivePrice)
            : t('pricing:pricing.panel.unresolved')}
        </Text>
      </div>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: token.fontSizeSM, marginTop: token.marginXXS }}
      >
        {t('pricing:pricing.panel.asOf', { date: resolution.asOf })}
        {snapshot ? ` · ${t('pricing:pricing.panel.snapshotNote')}` : ''}
      </Text>

      {/* Manual override — BOTH numbers stay visible (anti-175% artifact) */}
      {overridden && (
        <div
          style={{
            marginTop: token.marginSM,
            padding: token.paddingSM,
            backgroundColor: token.colorFillQuaternary,
            borderRadius: token.borderRadiusLG,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <Tag color="orange" style={{ margin: 0 }}>
              {t('pricing:pricing.panel.overridden')}
            </Tag>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.panel.systemPrice')}:{' '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {resolution.unitPrice !== null
                  ? formatMoney(displayCurrency, resolution.unitPrice)
                  : t('pricing:pricing.panel.unresolved')}
              </span>
            </Text>
          </div>
          <Text
            type="secondary"
            style={{ display: 'block', fontSize: token.fontSizeSM, marginTop: token.marginXXS }}
          >
            {t('pricing:pricing.panel.overrideReason')}: {overrideReason}
          </Text>
        </div>
      )}

      <Divider style={{ margin: `${token.marginSM}px 0` }} />

      {/* 2 — Tier walk in resolution order */}
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: token.marginXXS }}
      >
        {t('pricing:pricing.panel.tierWalkTitle')}
      </Text>
      {resolution.trace.map((entry) => (
        <TraceRow
          key={entry.tier}
          tier={entry.tier}
          matched={entry.matched}
          won={entry.won}
          candidatePrice={entry.candidatePrice}
          ruleRef={entry.won ? entry.ruleRef : undefined}
          skipReason={entry.skipReason}
        />
      ))}

      <Divider style={{ margin: `${token.marginSM}px 0` }} />

      {/* 3 — Build-up waterfall */}
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: token.marginXS }}
      >
        {t('pricing:pricing.panel.waterfallTitle')}
      </Text>
      {resolution.waterfall.length > 0 ? (
        <TableContainer>
          <Table<PriceWaterfallRow>
            rowKey="key"
            columns={waterfallColumns}
            dataSource={resolution.waterfall}
            pagination={false}
            size="small"
          />
        </TableContainer>
      ) : (
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t('pricing:pricing.panel.noWaterfall')}
        </Text>
      )}

      {/* 4 — Margin footer + red below-floor badge */}
      {margin && (
        <>
          <Divider style={{ margin: `${token.marginSM}px 0` }} />
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.panel.marginVsLanded')}
            </Text>
            <div className="tw-flex tw-items-center tw-gap-2">
              {belowFloor && (
                <Tag color="red" style={{ margin: 0 }}>
                  {t('pricing:pricing.panel.belowFloor')}
                </Tag>
              )}
              <Text
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  color: margin.amount >= 0 ? token.colorSuccess : token.colorError,
                }}
              >
                {formatAmount(margin.amount)} ({formatAmount(margin.pct)}%)
              </Text>
            </div>
          </div>
          {/* Minimum margin (2026-09-15): margin on price vs the company floor,
              and the markup on cost spelled out so the two are never confused. */}
          {floor.markupPct !== null && (
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-1">
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('pricing:pricing.panel.markupOnCost')}
              </Text>
              <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(floor.markupPct)}%
              </Text>
            </div>
          )}
          {/* Only the BELOW state renders — a floor row on every trace would be
              status noise (ux-lens anti-pattern 13). */}
          {floor.status === 'below_floor' && floor.floorPct !== null && (
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-1">
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('pricing:pricing.panel.minimumMargin')}
              </Text>
              <div className="tw-flex tw-items-center tw-gap-2">
                <Tag color="orange" style={{ margin: 0 }}>
                  {t('pricing:pricing.panel.belowMinimumMargin')}
                </Tag>
                <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatAmount(floor.floorPct)}%
                </Text>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drawer wrapper (SO-line trigger + product tab open this)
// ---------------------------------------------------------------------------

export interface PriceTracePanelProps extends PriceTraceContentProps {
  open: boolean;
  onClose: () => void;
}

const PriceTracePanel = ({ open, onClose, ...content }: PriceTracePanelProps) => {
  const { t } = useTranslation(['pricing']);
  return (
    <Drawer
      title={t('pricing:pricing.panel.title')}
      width={480}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <PriceTraceContent {...content} />
    </Drawer>
  );
};

export default PriceTracePanel;
